import { useTranslation } from "react-i18next";
import { Globe, Check } from "lucide-react";
import { SUPPORTED_LANGUAGES } from "@/i18n";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Inline language switcher rendered inside the avatar dropdown. Persists
 * to /api/me/language and flips i18n live (no reload).
 */
export default function LanguageSwitcher() {
  const { i18n, t } = useTranslation();
  const { user, refreshMe } = useAuth();
  const current = (i18n.language || "es").split("-")[0];

  const handleSelect = async (code) => {
    if (code === current) return;
    try {
      await i18n.changeLanguage(code);
      if (user) {
        await api.post("/me/language", { language: code });
        if (refreshMe) await refreshMe();
      }
    } catch (e) {
      console.warn("language switch failed", e);
    }
  };

  return (
    <div className="px-3 py-2" data-testid="language-switcher">
      <div className="flex items-center gap-2 px-1 mb-1.5">
        <Globe className="h-3.5 w-3.5 text-[#9CA3AF]" strokeWidth={1.75} />
        <span className="text-[10.5px] uppercase tracking-[0.18em] text-[#9CA3AF]">
          {t("dropdown.language")}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        {SUPPORTED_LANGUAGES.map((lang) => {
          const active = current === lang.code;
          return (
            <button
              key={lang.code}
              type="button"
              onClick={() => handleSelect(lang.code)}
              data-testid={`lang-option-${lang.code}`}
              data-active={active ? "true" : "false"}
              className={`
                inline-flex items-center justify-between gap-1.5 px-2.5 h-8 rounded-lg
                text-[11.5px] transition-colors
                ${active
                  ? "bg-[rgba(0,229,255,0.12)] border border-[rgba(0,229,255,0.4)] text-[#00E5FF]"
                  : "bg-white/[0.03] border border-white/[0.06] text-[#E5E7EB] hover:bg-white/[0.06]"}
              `}
            >
              <span className="flex items-center gap-1.5 truncate">
                <span aria-hidden="true">{lang.flag}</span>
                <span className="truncate">{lang.label}</span>
              </span>
              {active ? (
                <Check className="h-3 w-3 shrink-0" strokeWidth={2.5} />
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}
